import { Link } from "@tanstack/react-router";
import { ArrowRight, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { BOOKING_URL, CONTACT_PATH, routeTo } from "@/lib/site";
import { ACTIVITIES_HERO } from "./galleryData";

export function ActivitiesHero() {
  const photo = ACTIVITIES_HERO;

  return (
    <section
      className="relative isolate bg-forest-deep pb-16 sm:pb-20 lg:pb-24"
      aria-labelledby="activities-hero-title"
    >
      <div className="absolute inset-0 -z-10 overflow-hidden">
        <img
          src={photo.src}
          srcSet={photo.srcSet}
          sizes="100vw"
          alt={photo.alt}
          width={photo.width}
          height={photo.height}
          fetchPriority="high"
          decoding="async"
          className="hero-zoom size-full object-cover object-[center_60%]"
        />
        <div
          className="absolute inset-0 bg-[linear-gradient(180deg,oklch(0.259_0.042_163.6/0.9)_0%,oklch(0.259_0.042_163.6/0.68)_42%,oklch(0.259_0.042_163.6/0.92)_100%)]"
          aria-hidden="true"
        />
      </div>

      <div className="mx-auto max-w-[1400px] px-4 pt-10 sm:px-6 sm:pt-14 lg:px-10 lg:pt-16">
        <div className="hero-stagger max-w-3xl">
          <div style={{ "--stagger": 0 } as React.CSSProperties}>
            <Breadcrumb>
              <BreadcrumbList className="text-cream/75">
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link to="/" className="link-underline text-cream/80 hover:text-cream">
                      Home
                    </Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator className="text-cream/45 [&>svg]:size-3.5">
                  <ChevronRight />
                </BreadcrumbSeparator>
                <BreadcrumbItem>
                  <BreadcrumbPage className="font-medium text-cream">Activities</BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
          </div>

          <p className="eyebrow mt-8 text-sand" style={{ "--stagger": 1 } as React.CSSProperties}>
            Gather, Celebrate and Explore
          </p>

          <h1
            id="activities-hero-title"
            className="mt-4 text-balance font-display text-3xl leading-[1.08] text-cream sm:text-4xl lg:text-[3.35rem]"
            style={{ "--stagger": 2 } as React.CSSProperties}
          >
            Activities and Memorable Moments at Amigo Inn
          </h1>

          <p
            className="mt-5 max-w-2xl text-base leading-relaxed text-cream/85 sm:text-lg"
            style={{ "--stagger": 3 } as React.CSSProperties}
          >
            From family reunions and birthday celebrations to fishing trips, seasonal gatherings,
            and relaxing time with friends, Amigo Inn &amp; RV Park gives guests room to enjoy their
            stay in Zapata.
          </p>

          <div
            className="mt-8 flex flex-wrap items-center gap-4"
            style={{ "--stagger": 4 } as React.CSSProperties}
          >
            <Button asChild variant="book" size="ctaLg">
              <Link to={routeTo(CONTACT_PATH)}>
                Plan a Group Stay <ArrowRight aria-hidden="true" />
              </Link>
            </Button>
            <Button asChild variant="outlineLight" size="ctaLg">
              <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
                Book Your Stay
              </a>
            </Button>
          </div>

          <p
            className="mt-6 max-w-xl text-sm leading-relaxed text-cream/70"
            style={{ "--stagger": 5 } as React.CSSProperties}
          >
            A welcoming place for family reunions, birthday celebrations, fishing groups, holiday
            gatherings, and so much more.
          </p>
        </div>
      </div>
    </section>
  );
}
